import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  GlobalStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { useStore } from "src/Shared/Hooks";
import CompanyService from "../../Services/Company.service";
import { IRouteParams } from "../../Interfaces";
import {
  IOnboardingSopBlock,
  IOnboardingSopDocument,
  IOnboardingSopLine,
  IOnboardingSopTable,
} from "../../Interfaces/IOnboardingOutputs";
import {
  copyToClipboard,
  downloadJsonFile,
  formatOutputDateTime,
  replaceDateTimePlaceholders,
} from "../../Tools/OnboardingOutputs";

/**
 * The SOP output (Feature #5615, stories 5630/5634/5636).
 *
 * The document is the browser's print of this view: A4 pages with 14 mm margins, the app chrome and
 * the action bar suppressed, and each block kept together across a page break where it fits. The
 * eleven blocks and every value come from the server, built from the stored onboarding — the same
 * source the copy view and the JSON export read, so no output can drift from another.
 *
 * Opened before submission it renders in full as a clearly marked draft rather than an error, which
 * is the view a relationship manager reads back to the client. Rendering or reprinting writes
 * nothing to the record.
 */
const OnboardingSopOutput: React.FC = () => {
  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const [document, setDocument] = useState<IOnboardingSopDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const {
    rootStore: {
      companyStore: { setOpenClientContext },
    },
  } = useStore();
  const [notice, setNotice] = useState<string>("");

  useEffect(() => {
    if (!companyRowID) return;
    CompanyService.getOnboardingSopDocument(companyRowID).subscribe({
      next: (data) => {
        setDocument(data);
        setLoading(false);
        // Client-scoped route with no client record behind it: publish the open client so the app
        // bar lights PROSPECTS or HOME correctly however this page was reached (bug 5688).
        if (data && companyRowID) {
          setOpenClientContext({
            companyRowID,
            clientStatusID: data.clientStatusID,
          });
        }
      },
      error: (error: AxiosError) => {
        setLoading(false);
        ToastService.axiosError(error);
      },
    });
  }, [companyRowID]);

  const announce = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const onCopyEmailText = async () => {
    const result = await copyToClipboard(
      `${replaceDateTimePlaceholders(document?.emailSubject)}\n\n${replaceDateTimePlaceholders(document?.emailText)}`,
      "Email text",
    );
    announce(result.message);
    if (!result.ok) ToastService.error(result.message);
  };

  const onDownloadJson = () => {
    CompanyService.getOnboardingExport(companyRowID).subscribe({
      next: (data) => downloadJsonFile(data.fileName, data.json),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!document) {
    return <Alert severity="error">This client's SOP output could not be loaded.</Alert>;
  }

  return (
    <>
      {/* A4 with 14 mm margins; app chrome, the action bar and links are suppressed in print, and a
          block is kept whole across a page break where it fits (story 5630). */}
      <GlobalStyles
        styles={{
          "@media print": {
            "@page": { size: "A4", margin: "14mm" },
            "header, nav, aside, .MuiAppBar-root, .sop-no-print": { display: "none !important" },
            body: { backgroundColor: "#fff" },
            ".sop-block": { breakInside: "avoid", pageBreakInside: "avoid" },
            ".sop-sheet": { boxShadow: "none", padding: 0, margin: 0, maxWidth: "none" },
          },
        }}
      />

      <Box className="sop-no-print" sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print SOP
        </Button>
        <Button variant="outlined" onClick={onDownloadJson}>
          Download JSON
        </Button>
        <Button variant="outlined" onClick={onCopyEmailText}>
          Copy Email Text
        </Button>
        <Button component={Link} to={`/company/${companyRowID}/onboarding/setup-copy`}>
          Setup copy view
        </Button>
        <Button component={Link} to={`/company/${companyRowID}/overview`}>
          Back to client record
        </Button>
        {notice && <Chip size="small" color="success" label={notice} />}
      </Box>

      {/* Screen-only: a draft says so on screen, and the header says so in print (S7.12). */}
      {document.isDraft && (
        <Alert severity="warning" className="sop-no-print" sx={{ mb: 2 }}>
          This onboarding has not been submitted. This is a draft preview — read it back to the
          client, then submit from step 7 of the wizard.
        </Alert>
      )}

      <Box
        className="sop-sheet"
        sx={{
          backgroundColor: "#fff",
          maxWidth: 900,
          mx: "auto",
          p: 4,
          border: "1px solid #e0e0e0",
        }}
      >
        {document.blocks.map((block) => (
          <SopBlock key={block.key} block={block} document={document} />
        ))}
      </Box>
    </>
  );
};

const SopBlock: React.FC<{
  block: IOnboardingSopBlock;
  document: IOnboardingSopDocument;
}> = ({ block, document }) => {
  const isHeader = block.key === "header";
  const isFooter = block.key === "footer";

  /**
   * The block's lines and tables in the order they are meant to print.
   *
   * Every table used to print after every line, so block 5's Type of Testing matrix and its Hair &
   * Nail table — the two the setup team reads first — landed at the foot of the block rather than
   * directly under Service Line (bug 5711). Each table carries the number of lines that precede it,
   * so the builder's own ordering is honoured here.
   */
  const ordered = useMemo(() => {
    type Item =
      | { kind: "line"; line: IOnboardingSopLine; key: string }
      | { kind: "table"; table: IOnboardingSopTable; key: string };

    const items: Item[] = [];
    const tables = block.tables
      .map((table, index) => ({ table, index }))
      .sort((a, b) => (a.table.afterLineCount ?? 0) - (b.table.afterLineCount ?? 0));

    let cursor = 0;
    const emitLinesUpTo = (limit: number) => {
      for (; cursor < Math.min(limit, block.lines.length); cursor += 1) {
        items.push({
          kind: "line",
          line: block.lines[cursor],
          key: `line-${cursor}`,
        });
      }
    };

    tables.forEach(({ table, index }) => {
      emitLinesUpTo(Math.max(table.afterLineCount ?? 0, cursor));
      items.push({ kind: "table", table, key: `table-${index}` });
    });
    emitLinesUpTo(block.lines.length);

    return items;
  }, [block]);

  return (
    <Box className="sop-block" sx={{ mb: isHeader ? 3 : 2.5 }}>
      {isHeader ? (
        <>
          <Typography variant="overline" color="text.secondary">
            TDDA
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {block.title}
          </Typography>
          {document.isDraft && (
            <Chip size="small" color="warning" label="DRAFT — not yet submitted" sx={{ mt: 0.5 }} />
          )}
        </>
      ) : (
        !isFooter &&
        block.title && (
          <>
            <Typography variant="subtitle1" fontWeight={700}>
              {block.title}
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </>
        )
      )}

      {block.paragraph && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {block.paragraph}
        </Typography>
      )}

      {ordered.map((item) =>
        item.kind === "line" ? (
          item.line.isHeading ? (
          <Typography
            key={item.key}
            variant="subtitle2"
            fontWeight={600}
            sx={{ mt: 1 }}
          >
            {item.line.label}
          </Typography>
        ) : (
          <Box
            key={item.key}
            sx={{ display: "flex", gap: 2, py: 0.25, alignItems: "baseline" }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220 }}>
              {item.line.label}
            </Typography>
            <Box>
              <Typography variant="body2" component="span" sx={{ whiteSpace: "pre-line" }}>
                {replaceDateTimePlaceholders(item.line.value)}
              </Typography>
              {/* A per-value TBC flag, not only the block-3 instruction (S7.8). */}
              {item.line.tbc && (
                <Chip size="small" color="warning" variant="outlined" label="TBC" sx={{ ml: 1 }} />
              )}
              {item.line.caption && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.line.caption}
                </Typography>
              )}
            </Box>
          </Box>
          )
        ) : (
        <Box key={item.key} sx={{ mt: 1 }}>
          {item.table.title && (
            <Typography variant="subtitle2" fontWeight={600}>
              {item.table.title}
            </Typography>
          )}
          {item.table.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {item.table.emptyLine || "Not provided."}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {item.table.headers.map((header) => (
                    <TableCell key={header} sx={{ fontWeight: 600 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {item.table.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} sx={{ whiteSpace: "pre-line", verticalAlign: "top" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
        ),
      )}

      {/* A section with nothing captured prints its empty line rather than disappearing (S7.8). */}
      {!block.paragraph && !block.lines.length && !block.tables.length && block.emptyLine && (
        <Typography variant="body2" color="text.secondary">
          {block.emptyLine}
        </Typography>
      )}

      {isFooter && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Generated {formatOutputDateTime(document.generatedOn)} · {document.reference}
        </Typography>
      )}
    </Box>
  );
};

export default OnboardingSopOutput;
